import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "jx_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // installed — never show
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const recentlyDismissed = Date.now() - dismissedAt < DISMISS_DAYS * 864e5;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!recentlyDismissed) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => { setShow(false); setDeferred(null); });

    // iOS has no beforeinstallprompt — offer manual instructions
    if (isIOS() && !recentlyDismissed) {
      const t = setTimeout(() => setShow(true), 2500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setIosHelp(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setShow(false);
      setDeferred(null);
    } else if (isIOS()) {
      setIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-24 lg:bottom-6 inset-x-0 mx-auto z-[60] w-[94%] max-w-md animate-fadeup" data-testid="pwa-install-banner">
      <div className="jx-glass rounded-2xl p-4 jx-glow-purple border border-purple-500/30">
        {!iosHelp ? (
          <div className="flex items-start gap-3">
            <img src="/icon-192.png" className="w-12 h-12 rounded-xl" alt="JixxeoX" />
            <div className="flex-1">
              <p className="font-bold font-display">Take JixxeoX With You</p>
              <p className="text-xs text-slate-400 mt-0.5">Install the app for quick access to your music studio and favorite songs.</p>
              <div className="flex gap-2 mt-3">
                <button onClick={install} data-testid="pwa-install-btn" className="jx-cta text-white text-sm font-semibold px-4 py-2 rounded-xl flex items-center gap-2">
                  <Download size={15} /> Install App
                </button>
                <button onClick={dismiss} data-testid="pwa-dismiss-btn" className="text-sm text-slate-300 px-4 py-2 rounded-xl hover:bg-white/5">Not Now</button>
              </div>
            </div>
            <button onClick={dismiss} className="text-slate-500"><X size={16} /></button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold font-display">Install on iPhone / iPad</p>
              <button onClick={dismiss} className="text-slate-500"><X size={16} /></button>
            </div>
            <ol className="text-sm text-slate-300 space-y-1.5 list-decimal list-inside">
              <li>Tap the <Share size={14} className="inline mb-0.5" /> Share button in Safari.</li>
              <li>Scroll and tap <b>Add to Home Screen</b>.</li>
              <li>Tap <b>Add</b> — JixxeoX appears on your home screen.</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
